import { supabase } from '@/lib/supabase';

export interface CommentWithUser {
  id: string;
  expense_id: string;
  group_id: string;
  user_id: string;
  comment_text: string;
  created_at: string;
  updated_at: string | null;
  user: {
    id: string;
    display_name: string;
    avatar_url?: string;
  } | null;
}

export async function fetchExpenseComments(expenseId: string): Promise<CommentWithUser[]> {
  const { data, error } = await supabase
    .from('expense_comments')
    .select(`
      id, expense_id, group_id, user_id, comment_text, created_at, updated_at,
      user:users(id, display_name, avatar_url)
    `)
    .eq('expense_id', expenseId)
    .eq('_deleted', false)
    .order('created_at', { ascending: true });

  if (error) throw error;
  return (data ?? []).map((c: any) => ({
    ...c,
    user: Array.isArray(c.user) ? c.user[0] ?? null : c.user ?? null,
  })) as CommentWithUser[];
}

export async function addComment(
  expenseId: string,
  groupId: string,
  userId: string,
  commentText: string,
): Promise<void> {
  // `_modified` is intentionally NOT sent: it is the replication checkpoint
  // column and must come from the server clock (DEFAULT now() / moddatetime).
  // A browser clock running ahead of the server would stamp a future value
  // and make mobile clients skip every later server-side edit of this row.
  const { error } = await supabase.from('expense_comments').insert({
    id: crypto.randomUUID(),
    expense_id: expenseId,
    group_id: groupId,
    user_id: userId,
    comment_text: commentText,
    created_at: new Date().toISOString(),
  });
  if (error) throw error;
}

export async function updateComment(commentId: string, commentText: string): Promise<void> {
  const now = new Date().toISOString();
  const { error } = await supabase
    .from('expense_comments')
    .update({ comment_text: commentText, updated_at: now })
    .eq('id', commentId);
  if (error) throw error;
}

export async function deleteComment(commentId: string): Promise<void> {
  const { error } = await supabase
    .from('expense_comments')
    .update({ _deleted: true })
    .eq('id', commentId);
  if (error) throw error;
}

/**
 * Fetches comment counts for all expenses in a group in a single query.
 * Returns a map of expenseId → count.
 */
export async function fetchGroupCommentCounts(
  groupId: string,
): Promise<Record<string, number>> {
  const { data, error } = await supabase
    .from('expense_comments')
    .select('expense_id')
    .eq('group_id', groupId)
    .eq('_deleted', false);

  if (error) throw error;

  const counts: Record<string, number> = {};
  for (const row of data ?? []) {
    counts[row.expense_id] = (counts[row.expense_id] ?? 0) + 1;
  }
  return counts;
}

/**
 * Fetches comment counts for a list of expense IDs (cross-group).
 * Returns a map of expenseId → count.
 */
export async function fetchCommentCountsByExpenseIds(
  expenseIds: string[],
): Promise<Record<string, number>> {
  if (expenseIds.length === 0) return {};

  const { data, error } = await supabase
    .from('expense_comments')
    .select('expense_id')
    .in('expense_id', expenseIds)
    .eq('_deleted', false);

  if (error) throw error;

  const counts: Record<string, number> = {};
  for (const row of data ?? []) {
    counts[row.expense_id] = (counts[row.expense_id] ?? 0) + 1;
  }
  return counts;
}
